// Plain-English definitions for the 3 pillars and 4 derived indicators.
// Single source of truth for the Methodology section and the metric drawer.

import type { DerivedMetricKey, FatigueRiskTier } from './types';

export interface MetricDefinition {
  code: string;
  name: string;
  /** One-line framing question used on cards and in the methodology list. */
  question: string;

  // --- Description card: "What is X? / How is X calculated? / Why this matters" ---
  /** Plain-English "What is X?" — one or two sentences. */
  whatIs: string;
  /** "How is X calculated?" lead sentence. */
  howCalculated: string;
  /** Optional bullet list of calculation factors (LRM / CDI / PDC). */
  howCalculatedList?: string[];
  /** Pillar(s) this indicator relates to, e.g. "Sleep Opportunity & Quality + Readiness". */
  relatedTo: string;
  /** "Why this matters" — one paragraph per line. */
  whyMatters: string[];

  // --- "{CODE} Breakdown" modal: "Understanding X" ---
  breakdownHeading: string;
  breakdownIntro: string;
  /** Lead-in for the "higher score" bullet list. */
  higherScoreLabel: string;
  higherScoreBullets: string[];
  /** "Use X to help identify" — rendered as a ✓ checklist. */
  useToIdentify: string[];
  /** Closing scope reminder shown in the breakdown modal. */
  breakdownCaveat: string;

  // --- Headline card copy (FM-1) ---
  /**
   * One short line for the headline card. FM-1: the top boxes carried the full
   * "whatIs" paragraph, which was too much to read at a glance — the paragraph
   * now lives in the breakdown panel and the card shows this instead.
   */
  cardSummary: string;
  /** The single clarifying sentence TW asked to be added per box (FM-1). */
  cardNote: string;
}

export const PILLARS = [
  {
    title: '1 · Sleep Opportunity & Quality',
    body: 'Did the schedule give people enough opportunity to sleep, and was the sleep they got restorative? Combines sleep duration vs need, consistency of timing, and accumulated sleep debt.',
  },
  {
    title: '2 · Readiness / physiological capacity today',
    body: "How much capacity does the body have to take on load today? Built from recovery, heart-rate variability and resting heart rate relative to each person's own baseline.",
  },
  {
    title: '3 · Load & Timing / job demand',
    body: 'What is the job actually asking of people, and when? Combines day strain/load with rota context — shift type, start time, overtime and rest periods.',
  },
];

export const METRIC_DEFINITIONS: Record<DerivedMetricKey, MetricDefinition> = {
  fed: {
    code: 'FED',
    name: 'Fatigue Exposure Days',
    question: 'How often are people starting work already fatigued?',
    whatIs:
      'Fatigue Exposure Days shows how often people are starting work already fatigued. It identifies periods where employees begin a shift with reduced readiness due to factors such as limited sleep opportunity, poor sleep quality or accumulated sleep debt.',
    howCalculated:
      'A fatigue exposure day is counted when readiness is low and/or sleep debt is high before the start of a shift. FED is reported as fatigue exposure days per 1,000 workdays, allowing fair comparison between teams of different sizes.',
    relatedTo: 'Sleep Opportunity & Quality + Readiness',
    whyMatters: [
      'FED helps identify when people are starting work without enough recovery capacity.',
      'Poor sleep opportunity, inconsistent sleep patterns or accumulated sleep debt can reduce readiness before a shift begins.',
      'By monitoring FED, teams can understand whether fatigue exposure is becoming a recurring pattern and identify opportunities to improve recovery and scheduling.',
    ],
    breakdownHeading: 'Understanding Fatigue Exposure Days',
    breakdownIntro: 'Fatigue Exposure Days identify how often employees begin work with reduced recovery capacity.',
    higherScoreLabel: 'A higher FED score may indicate increased fatigue exposure due to factors such as:',
    higherScoreBullets: [
      'insufficient sleep opportunity',
      'demanding schedules',
      'limited recovery time',
      'challenging shift patterns',
    ],
    useToIdentify: [
      'Teams or periods with higher fatigue exposure',
      'Whether fatigue exposure is improving over time',
      'Opportunities to improve recovery and scheduling',
    ],
    breakdownCaveat: 'FED highlights workforce patterns and does not assess individual health or ability to work.',
    cardSummary: 'How often people start work already fatigued.',
    cardNote: 'It is reported as fatigue exposure days per 1,000 workdays, so teams of different sizes compare fairly.',
  },
  lrm: {
    code: 'LRM',
    name: 'Load-Readiness Misalignment',
    question: 'Was the work demand appropriate for their physical state?',
    whatIs:
      'Load-Readiness Misalignment shows when work demands are higher than current readiness levels. It identifies situations where people are exposed to demanding work when recovery capacity may be reduced.',
    howCalculated: 'LRM measures the percentage of shifts where:',
    howCalculatedList: ['Workload or strain is high', 'Readiness is low'],
    relatedTo: 'Load & Timing + Readiness',
    whyMatters: [
      'LRM shows when work demands and recovery capacity are not aligned.',
      'A demanding shift may be manageable when someone is well recovered, but the same workload can create greater fatigue when readiness is reduced.',
      'LRM helps identify when workload, timing and recovery need closer attention.',
    ],
    breakdownHeading: 'Understanding Load-Readiness Misalignment',
    breakdownIntro: 'LRM identifies when work demands may exceed current recovery capacity.',
    higherScoreLabel: 'A higher LRM score means more occasions where:',
    higherScoreBullets: [
      'workload is high',
      'readiness is reduced',
      'recovery may not match the demands being placed on people',
    ],
    useToIdentify: [
      'Periods of increased workload pressure',
      'Teams experiencing greater recovery demands',
      'Whether workload and recovery are balanced effectively',
    ],
    breakdownCaveat:
      'LRM helps identify opportunities to adjust systems and schedules, rather than evaluate individuals.',
    cardSummary: 'Share of shifts where demand outran recovery capacity.',
    cardNote: 'This highlights occasions where workload and recovery may not be well matched.',
  },
  cdi: {
    code: 'CDI',
    name: 'Circadian Disruption Index',
    question: 'Is the schedule itself disrupting sleep and recovery?',
    whatIs:
      'Circadian Disruption Index shows the impact of work schedules on sleep timing and consistency. It identifies whether shift patterns may be making regular sleep and recovery more difficult.',
    howCalculated:
      'CDI combines the following factors that influence sleep regularity and recovery to create a score from 0–100:',
    howCalculatedList: ['Sleep timing variation', 'Sleep consistency', 'Repeated sleep debt'],
    relatedTo: 'Sleep Opportunity & Quality',
    whyMatters: [
      'CDI highlights how working patterns and schedules influence sleep consistency.',
      'Frequent changes, night shifts and irregular schedules can make it harder for people to maintain regular sleep patterns and recover effectively.',
      'CDI helps teams understand whether scheduling design may be contributing to fatigue risk.',
    ],
    breakdownHeading: 'Understanding Circadian Disruption',
    breakdownIntro: "CDI shows how work schedules may influence the body's natural sleep-wake rhythm.",
    higherScoreLabel: 'Higher CDI scores are often associated with:',
    higherScoreBullets: [
      'frequent schedule changes',
      'night shifts',
      'inconsistent sleep timing',
      'reduced recovery opportunities',
    ],
    useToIdentify: [
      'Scheduling patterns affecting sleep consistency',
      'Opportunities to improve shift design',
      'Whether sleep disruption is improving over time',
    ],
    breakdownCaveat: 'CDI focuses on the impact of schedules, not personal sleep choices.',
    cardSummary: 'How much the schedule itself disrupts sleep timing.',
    cardNote: 'Higher scores indicate greater disruption to normal sleep patterns.',
  },
  pdc: {
    code: 'PDC',
    name: 'Physiological Deviation Cluster',
    question: 'Are multiple vitals drifting outside personal baseline together?',
    whatIs:
      "Physiological Deviation Cluster shows when multiple recovery signals change from someone's normal baseline at the same time. It identifies when several physiological measures move away from an individual's typical pattern.",
    howCalculated: 'PDC identifies when multiple physiological signals change together, such as:',
    howCalculatedList: ['Resting heart rate increasing', 'HRV decreasing', 'Respiratory rate increasing'],
    relatedTo: 'Readiness',
    whyMatters: [
      "PDC identifies when multiple physiological recovery signals change from an individual's normal baseline.",
      'Changes in measures such as resting heart rate, HRV and respiratory rate may indicate that recovery status is changing.',
      'PDC provides an early opportunity to review workload and recovery support, it is not a medical assessment.',
    ],
    breakdownHeading: 'Understanding Physiological Deviation Clusters',
    breakdownIntro:
      "PDC highlights when several body signals move away from an individual's normal pattern at the same time.",
    higherScoreLabel:
      'A higher PDC score may indicate that more people are showing changes in physiological recovery signals.',
    higherScoreBullets: [],
    useToIdentify: [
      'Early changes in recovery status',
      'Groups experiencing increased physiological strain',
      'Opportunities for supportive check-ins',
    ],
    breakdownCaveat: 'PDC is an early warning signal and should never be interpreted as a diagnosis.',
    cardSummary: 'People whose recovery signals are drifting together.',
    cardNote:
      'It is reported as the percentage of people showing changes in multiple recovery signals compared with their usual baseline.',
  },
};

export const WHY_DERIVED =
  'PEAK4 presents its own operational indicators (FED, LRM, CDI, PDC) rather than exposing raw WHOOP proprietary scores. These derived metrics are built for operational decisions — scheduling, workload and rest — are comparable across cohorts of different sizes, and keep the focus on cohort-level risk and controls rather than individual surveillance. Raw vendor scores are an input, not the product.';

// ── RAG ranges (FM-16) ──────────────────────────────────────────────────────
//
// PROVISIONAL. TW's fatigue material defines a RAG split for the workforce risk
// tier counts but no numeric bands for FED, LRM, CDI or PDC (see PEAK-185).
// These bands are reverse-engineered from the tiers the fixture data already
// asserts, so the dashboard is at least internally consistent: every tier shown
// anywhere is computed from these bands via tierForMetric, rather than being
// hardcoded per row. Replace the numbers once TW confirms them — nothing else
// needs to change.

export interface RagBand {
  tier: FatigueRiskTier;
  /** Inclusive lower bound. */
  from: number;
  /** Inclusive upper bound; null = open-ended. */
  to: number | null;
}

export interface RagRanges {
  /** Short unit shown next to each band, e.g. "per 1,000 workdays". */
  unit: string;
  bands: RagBand[];
}

export const RAG_RANGES: Record<DerivedMetricKey, RagRanges> = {
  fed: {
    unit: 'per 1,000 workdays',
    bands: [
      { tier: 'green', from: 0, to: 39 },
      { tier: 'amber', from: 40, to: 79 },
      { tier: 'red', from: 80, to: null },
    ],
  },
  lrm: {
    unit: '% of shifts',
    bands: [
      { tier: 'green', from: 0, to: 19 },
      { tier: 'amber', from: 20, to: 44 },
      { tier: 'red', from: 45, to: null },
    ],
  },
  cdi: {
    unit: 'score (0–100)',
    bands: [
      { tier: 'green', from: 0, to: 34 },
      { tier: 'amber', from: 35, to: 54 },
      { tier: 'red', from: 55, to: null },
    ],
  },
  pdc: {
    unit: '% of workforce',
    bands: [
      { tier: 'green', from: 0, to: 9 },
      { tier: 'amber', from: 10, to: 16 },
      { tier: 'red', from: 17, to: null },
    ],
  },
};

export const RAG_PROVISIONAL_NOTE =
  'Provisional ranges — derived from the tiers in the current model and awaiting confirmation from the fatigue lead (PEAK-185).';

/** Format one band as a range label, e.g. "40–79" or "80+". */
export const formatBand = (band: RagBand): string => (band.to === null ? `${band.from}+` : `${band.from}–${band.to}`);

/** RAG tier for a value of a given indicator, per RAG_RANGES. */
export const tierForMetric = (key: DerivedMetricKey, value: number): FatigueRiskTier => {
  const bands = RAG_RANGES[key].bands;
  const hit = bands.find((b) => value >= b.from && (b.to === null || value <= b.to));
  // Values below the first band's floor are as good as it gets; above the last
  // band's ceiling can only happen if a band list is misconfigured.
  return hit?.tier ?? (value < bands[0].from ? bands[0].tier : bands[bands.length - 1].tier);
};

/** Worst tier of a set — used for a cohort's overall tier. */
export const worstTier = (tiers: FatigueRiskTier[]): FatigueRiskTier =>
  tiers.includes('red') ? 'red' : tiers.includes('amber') ? 'amber' : 'green';

/**
 * Bands for a person's composite 0–100 fatigue score (FM-8 / FM-9).
 *
 * Separate from RAG_RANGES because the score is not one of the four indicators —
 * it is a weighted blend of them, so none of their bands applies to it. Equally
 * provisional and equally awaiting PEAK-185.
 */
export const INDIVIDUAL_SCORE_BANDS: RagBand[] = [
  { tier: 'green', from: 0, to: 44 },
  { tier: 'amber', from: 45, to: 69 },
  { tier: 'red', from: 70, to: null },
];

/** RAG tier for a person's composite score. Unscorable people read as green. */
export const tierForScore = (score: number | null): FatigueRiskTier => {
  if (score === null) return 'green';
  return INDIVIDUAL_SCORE_BANDS.find((b) => score >= b.from && (b.to === null || score <= b.to))?.tier ?? 'green';
};

/** Confidence at or above which a cohort counts as "reliable data" (FM-15). */
export const RELIABLE_CONFIDENCE_MIN = 75;

export const NOT_MEDICAL =
  'This dashboard supports operational fatigue risk management. It is not a medical device and does not provide medical diagnosis. Indicators describe fatigue exposure and workload alignment at cohort level, and are intended to prompt support, rest and workload adjustment.';
